import { createRouter, createWebHistory } from 'vue-router'
import {MODULE_MAPPING, TOKEN} from "@/constants";
import {useRequest} from "@/composables/useRequest";
import usePermissions from "@/composables/usePermissions";
import {AlertConfigurations} from "@/utilities/alerts.utilities";

let condition = true
const {
  canBeAccessed
} = usePermissions()

const routes = [
  {
    path: '/login',
    name: 'index',
    component: () => import('../views/Index.vue')
  },
  {
    path: '/',
    redirect: {name: 'dashboard'},
    meta: {requiresAuth: true},
    component: () => import('../views/ContentWrapper.vue'),
    children: [
      {
        path: '/dashboard',
        name: 'dashboard',
        meta: {title: 'Dashboard', requireAccess: true},
        component: () => import('../views/Dashboard.vue'),
      },
      {
        path: '/request-for-payment',
        name: 'requestForPayment',
        redirect: {name: 'requestForPaymentList'},
        meta: {requireAccess: true},
        component: () => import('../components/layout/Container.vue'),
        children: [
          {
            path: 'all-requests',
            name: 'allRequestsPage',
            meta: {title: 'All Requests'},
            component: () => import('../components/layout/Container.vue'),
            children: [
              {
                path: '',
                name: 'requestForPaymentList',
                meta: {title: 'Request for Payment'},
                component: () => import('../views/modules/rfp/RequestForPaymentListing.vue'),
              },
              {
                path: ':code',
                name: 'newRequestForPayment',
                props: true,
                meta: {title: 'Modify Request for Payment'},
                beforeEnter(to, from, next){
                  condition = canBeAccessed('rfp', 'create')
                  if(to.params.code !== 'new') condition = canBeAccessed('rfp', 'module')

                  if(!condition) next({name: 'accessDenied'})
                  else next()
                },
                component: () => import('../views/modules/rfp/NewRequestForPayment.vue'),
              },
            ]
          },
          {
            path: 'unsigned-requests',
            name: 'unsignedRequestsPage',
            meta: {title: 'Unsigned Request'},
            component: () => import('../components/layout/Container.vue'),
            children: [
              {
                path: '',
                name: 'unsignedRequests',
                meta: {title: 'Unsigned Request'},
                component: () => import('../views/modules/rfp/UnsignedRequestList.vue'),
              },
              {
                path: ':code',
                name: 'viewUnsignedRequest',
                props: true,
                meta: {title: 'View Request'},
                beforeEnter(to, from, next){
                  condition = canBeAccessed('rfp', 'module')

                  if(!condition) next({name: 'accessDenied'})
                  else next()
                },
                component: () => import('../views/modules/rfp/NewRequestForPayment.vue'),
              },
            ]
          },
        ]
      },
      {
        path: '/purchase-requisition',
        name: 'purchaseRequisition',
        component: () => import('../views/misc/OngoingDevelopment.vue'),
      },
      {
        path: '/purchase-order',
        name: 'purchaseOrder',
        component: () => import('../views/misc/OngoingDevelopment.vue'),
      },
      {
        path: '/users',
        name: 'users',
        redirect: {name: 'usersList', requireAccess: true},
        meta: {requireAccess: true},
        component: () => import('../components/layout/Container.vue'),
        children: [
          {
            path: '',
            name: 'usersList',
            meta: {title: 'Users'},
            component: () => import('../views/modules/Users.vue')
          },
          {
            path: ':code',
            name: 'modifyUserDetails',
            props: true,
            meta: {title: 'Users Details'},
            beforeEnter(to, from, next){
              condition = canBeAccessed('users', 'create')
              if(to.params.code !== 'new') condition = canBeAccessed('users', 'module')

              if(!condition) next({name: 'accessDenied'})
              else next()
            },
            component: () => import('../views/modules/Profile.vue')
          },
        ]
      },
      {
        path: '/profile/:code',
        name: 'profile',
        props: true,
        meta: {title: 'Profile'},
        component: () => import('../views/modules/Profile.vue'),
      },
      {
        path: '/signatories',
        name: 'signatories',
        props: true,
        meta: {title: 'Signatories'},
        beforeEnter(to, from, next){
          condition = canBeAccessed('signatories', 'module')

          if(!condition) next({name: 'accessDenied'})
          else next()
        },
        component: () => import('../views/modules/Signatories.vue')
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'Page404',
    component: () => import('../views/misc/PageNotFound.vue'),
  },
  {
    path: '/unauthorized',
    name: 'accessDenied',
    component: () => import('@/views/misc/PageUnauthorizedAccess.vue')
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(async(to, from, next) => {
  if(to.meta.requiresAuth) {
    if (!localStorage.getItem(TOKEN) && to.name !== "login") return next({name: "index"})
    else if(to.meta.requireAccess) {
      let module = to.matched[1].name
      if(!usePermissions().canBeAccessed(MODULE_MAPPING[module].code)) {
        let module = usePermissions().nearestAccessibleModule()

        if(module) next({name: module})
        else {
          await useRequest().logout()
          await AlertConfigurations.failedAlert('You do not have the permissions to access this service.', 'Unauthorized')
        }
      } else next()
    }
    else return next()

    let expirationDate = Date.parse(localStorage.getItem("expirationLimit"));

    if (Date.now() > expirationDate) {
      await useRequest().logout()
      return next({name: "index"})
    }
  } else if(localStorage.getItem(TOKEN) && to.name === 'index') {
    next({name: 'dashboard'})
  } else next()
  document.title = to.meta.title ? to.meta.title + ' - Fujitrans' : 'Fujitrans RFP System'
})

export default router
